if (!customElements.get('infinite-scroll')) {
  customElements.define(
    'infinite-scroll',
    class InfiniteScroll extends HTMLElement {
      constructor() {
        super();
        this.loading = false;
        this.onButtonClick = this.onButtonClick.bind(this);
        this.onIntersection = this.onIntersection.bind(this);
        this.onScroll = this.onScroll.bind(this);
      }

      connectedCallback() {
        this.button = this.querySelector('[data-infinite-scroll-button]');
        this.status = this.querySelector('[data-infinite-scroll-status]');
        this.spinner = this.querySelector('.loading__spinner');
        this.sentinel = this.querySelector('[data-infinite-scroll-sentinel]') || this;
        this.nextUrl = this.getCleanUrl(this.dataset.nextUrl);

        // facets.js re-renders the whole #ProductGridContainer, which disconnects this element and
        // connects a fresh one, so all paging state is (re)initialised here.
        if (!this.nextUrl) {
          this.finish(false);
          return;
        }

        if (this.button) {
          this.button.href = this.nextUrl;
          this.button.addEventListener('click', this.onButtonClick);
        }

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(this.onIntersection, {
            rootMargin: `0px 0px ${parseInt(this.dataset.rootMargin, 10) || 600}px 0px`,
          });
          this.observer.observe(this.sentinel);
        }
        // Fallback for browsers/contexts where the observer doesn't fire (e.g. some embedded webviews):
        // a passive, rAF-throttled scroll check against the same 600px margin.
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      onScroll() {
        if (this.scrollTicking || this.loading || !this.nextUrl) return;
        this.scrollTicking = true;
        requestAnimationFrame(() => {
          this.scrollTicking = false;
          const margin = parseInt(this.dataset.rootMargin, 10) || 600;
          if (this.sentinel.getBoundingClientRect().top - window.innerHeight < margin) this.loadMore();
        });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        this.observer?.disconnect();
        this.observer = null;
        this.abortController?.abort();
        this.button?.removeEventListener('click', this.onButtonClick);
      }

      onIntersection(entries) {
        if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
      }

      onButtonClick(event) {
        // Let modified clicks (new tab/window) fall through to the plain link.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        this.loadMore(true);
      }

      getCleanUrl(href) {
        if (!href) return null;
        const url = new URL(href, window.location.origin);
        // Section rendering requests (facets.js) can leak section_id into paginate.next.url.
        url.searchParams.delete('section_id');
        url.searchParams.delete('sections');
        return `${url.pathname}${url.search}`;
      }

      getGrid() {
        return this.closest('#ProductGridContainer')?.querySelector('.product-grid');
      }

      setLoading(isLoading) {
        this.loading = isLoading;
        this.setAttribute('aria-busy', isLoading);
        this.spinner?.classList.toggle('hidden', !isLoading);
        if (this.button) {
          this.button.classList.toggle('loading', isLoading);
          this.button.setAttribute('aria-disabled', isLoading);
        }
        if (isLoading) this.setStatus(this.dataset.loadingText);
      }

      setStatus(text = '') {
        if (this.status) this.status.textContent = text;
      }

      async loadMore(focusNewItems = false) {
        if (this.loading || !this.nextUrl) return;

        const grid = this.getGrid();
        if (!grid) return;

        this.setLoading(true);
        this.abortController = new AbortController();

        const url = new URL(this.nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);

        try {
          const response = await fetch(url.toString(), { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`Infinite scroll request failed: ${response.status}`);

          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (!this.isConnected) return;

          const newItems = this.appendItems(grid, html);
          const nextElement = html.querySelector('infinite-scroll');
          this.nextUrl = this.getCleanUrl(nextElement?.dataset.nextUrl);
          this.dataset.nextUrl = this.nextUrl || '';

          this.setLoading(false);
          this.setStatus();
          this.animateItems(newItems);
          if (focusNewItems) newItems[0]?.querySelector('a[href], button:not([disabled])')?.focus();

          if (!this.nextUrl) {
            this.finish(true);
            return;
          }

          if (this.button) this.button.href = this.nextUrl;

          // The observer only fires on changes, so re-observe in case the sentinel is still in range.
          if (this.observer) {
            this.observer.unobserve(this.sentinel);
            this.observer.observe(this.sentinel);
          }
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error(error);
          this.setLoading(false);
          // Stop auto-loading after a failure; the "Load more" link stays available as a retry.
          window.removeEventListener('scroll', this.onScroll);
          this.observer?.disconnect();
          this.observer = null;
          this.setStatus(this.dataset.errorText);
        }
      }

      appendItems(grid, html) {
        const sourceGrid = html.querySelector('#ProductGridContainer .product-grid');
        if (!sourceGrid) return [];

        const keys = new Set(
          Array.from(grid.children)
            .map((item) => item.dataset.itemKey)
            .filter(Boolean),
        );

        const newItems = Array.from(sourceGrid.children).filter((item) => {
          const key = item.dataset.itemKey;
          if (!key) return true;
          if (keys.has(key)) return false;
          keys.add(key);
          return true;
        });

        newItems.forEach((item) => {
          if (!item.classList.contains('scroll-trigger')) item.classList.add('infinite-scroll__item');
        });
        grid.append(...newItems);

        return newItems;
      }

      animateItems(items) {
        if (items.length === 0 || typeof initializeScrollAnimationTrigger !== 'function') return;

        const triggers = items.filter((item) => item.classList.contains('scroll-trigger'));
        if (triggers.length === 0) return;

        // Only observe the appended items; re-running on the whole grid would hide items above the fold.
        initializeScrollAnimationTrigger({ getElementsByClassName: () => triggers });
      }

      finish(announce) {
        window.removeEventListener('scroll', this.onScroll);
        this.observer?.disconnect();
        this.observer = null;
        this.button?.remove();
        this.button = null;
        this.setStatus(announce ? this.dataset.loadedText : '');
        this.classList.add('infinite-scroll--complete');
      }
    },
  );
}
